// Data for the Grammar tab's "Verb choice" topic: cases where Portuguese picks
// a different verb than English would, so the lesson is choosing the verb for
// the meaning, not conjugating it. Entries are indexed by the English phrase
// the learner starts from. Clusters, each a card with a one-line "decision" and a
// table, most common in everyday speech first. In the examples **double asterisks**
// mark the verb pattern shown in bold.

// [Portuguese, English]
export type VerbExample = [string, string]

// ── can / be able to ─────────────────────────────────────────────
export type CanRow = { english: string; portuguese: string; why: string }

export const CAN_ROWS: CanRow[] = [
  { english: '“I can’t open this”', portuguese: 'Não **consigo** abrir', why: 'Actual ability or success' },
  { english: '“Can you hear me?”', portuguese: '**Consegue** me ouvir?', why: 'Actual ability or success' },
  { english: '“May I...?”', portuguese: '**Posso**...?', why: 'Permission' },
  { english: '“I know how to read music”', portuguese: '**Sei** ler partituras', why: 'Learned skill' },
  { english: '“I can’t stop thinking about it”', portuguese: 'Não **consigo** parar de pensar nisso', why: 'Mental or emotional inability' },
  { english: '“I got the tickets”', portuguese: '**Consegui** os ingressos', why: 'Obtaining something (conseguir also means “to get”)' },
]

// ── to be — ser vs. estar vs. ficar ──────────────────────────────
export const BE_QUICK_TEST = 'Quick test: if it would still be true tomorrow, use ser; if it could change, use estar.'

export type BeVerb = 'ser' | 'estar' | 'ficar'

// The three columns of the "to be" card: the verb and what it answers. The **bold** words are
// what tells the three apart; the card has no description of its own, so these say it once.
export const BE_VERBS: Array<{ verb: BeVerb; gloss: string }> = [
  { verb: 'ser', gloss: 'what something **is**' },
  { verb: 'estar', gloss: 'how or where it is **right now**' },
  { verb: 'ficar', gloss: 'where a **place** is, or **staying put**' },
]
export type BeRow = { use: string; verb: BeVerb; english: string; portuguese: string }

export const BE_ROWS: BeRow[] = [
  { use: 'Identity / profession', verb: 'ser', english: '“She’s a doctor”', portuguese: 'Ela **é** médica' },
  { use: 'Personality / lasting trait', verb: 'ser', english: '“He’s annoying (by nature)”', portuguese: 'Ele **é** chato' },
  { use: 'Origin / nationality', verb: 'ser', english: '“I’m from Brazil”', portuguese: '**Sou** do Brasil' },
  { use: 'Time / date', verb: 'ser', english: '“It’s 3 o’clock”', portuguese: '**São** três horas' },
  { use: 'Where an event happens', verb: 'ser', english: '“The party is at my house”', portuguese: 'A festa **é** na minha casa' },
  { use: 'Physical or emotional state', verb: 'estar', english: '“I’m tired”', portuguese: '**Estou** cansado' },
  { use: 'Current location of a person or thing', verb: 'estar', english: '“I’m at home”', portuguese: '**Estou** em casa' },
  { use: 'Action in progress (+ gerund)', verb: 'estar', english: '“I’m studying”', portuguese: '**Estou** estudando' },
  { use: 'Current condition', verb: 'estar', english: '“The soup is cold”', portuguese: 'A sopa **está** fria' },
  { use: 'Temporary mood or behavior', verb: 'estar', english: '“He’s being annoying today”', portuguese: 'Ele **está** chato' },
  { use: 'Position right now', verb: 'estar', english: '“I’m sitting on the couch”', portuguese: '**Estou** sentado no sofá' },
  { use: 'Fixed location of a place', verb: 'ficar', english: '“The bank is on the corner”', portuguese: 'O banco **fica** na esquina' },
  { use: 'Staying in a position', verb: 'ficar', english: '“I stayed sitting on the couch”', portuguese: '**Fiquei** sentado no sofá' },
]

// ── have done / have been doing (ter + participle) ───────────────
export const PERFECT_QUICK_TEST =
  'Quick test: if adding “lately” or “these days” makes sense, use tenho + participle. If it happened once or it’s done, use the preterite.'

// `use` and each example star their verb pattern. `note` sits small under `meaning`.
export type PerfectRow = { meaning: string; note?: string; use: string; examples: VerbExample[] }

export const PERFECT_ROWS: PerfectRow[] = [
  {
    meaning: 'Finished',
    note: 'even when English says “have done”',
    use: 'Preterite',
    examples: [
      ['Já **comi**.', 'I’ve already eaten.'],
      ['Você **viu** o filme?', 'Have you seen the movie?'],
    ],
  },
  {
    meaning: 'Happening right now',
    use: '**estar** + gerund',
    examples: [['**Estou falando**.', 'I’m speaking.']],
  },
  {
    meaning: 'Continuous for a stretch of time (“for X years”)',
    use: 'Present + **faz** / **há**',
    examples: [['**Moro** aqui **faz** 3 anos.', 'I’ve been living here for 3 years.']],
  },
  {
    meaning: 'Had done (before another past event)',
    note: 'this one matches English',
    use: '**ter** (imperfect) + participle',
    examples: [['Eu **tinha saído**.', 'I had left.']],
  },
  {
    meaning: 'Repeated or ongoing lately, up to now',
    use: '**ter** (present) + participle',
    examples: [['**Tenho estudado** muito.', 'I’ve been studying a lot lately.']],
  }
]

// The participle in one line under the table (the full per-verb forms are on the Conjugations tab).
export const PARTICIPLE_RULE = '-ar → -ado · -er / -ir → -ido'

// [infinitive, participle] pairs that break the rule.
export const IRREGULAR_PARTICIPLES: Array<[string, string]> = [
  ['fazer', 'feito'],
  ['dizer', 'dito'],
  ['ver', 'visto'],
  ['escrever', 'escrito'],
  ['abrir', 'aberto'],
  ['pôr', 'posto'],
  ['vir', 'vindo'],
  ['pagar', 'pago'],
  ['ganhar', 'ganho'],
  ['gastar', 'gasto'],
]
